import { useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Grid } from '@opendoor/atoms/grid';
import { IconX } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';

import { CenteredLayout } from '../StandaloneText';
import { trapFocus } from './trapFocus';

const backdropVariants = {
  hidden: {
    opacity: 0,
    transition: {
      ease: [0.55, 0, 0.15, 1],
      delay: 0.5,
      duration: 0.2,
    },
  },
  visible: {
    opacity: 1,
    transition: {
      ease: [0.55, 0, 0.15, 1],
      duration: 0.2,
    },
  },
};

const boxVariants = {
  hidden: {
    y: '100%',
    transition: {
      ease: [0.55, 0, 0.15, 1],
      duration: 0.5,
    },
  },
  visible: {
    y: 0,
    transition: {
      ease: [0.55, 0, 0.15, 1],
      duration: 0.75,
    },
  },
};

interface ISheetContentProps {
  entry: any;
  setIsVisible: (isVisible: boolean) => void;
}

interface ISheetProps extends ISheetContentProps {
  exitComplete: () => void;
  isVisible?: boolean;
}

const SheetContent = ({ entry, setIsVisible }: ISheetContentProps) => {
  const dialogId = `dialog-${Math.floor(Math.random() * 100 + 1)}-${entry.sys.id}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const onComplete = (definition: any) => {
    // Only focus on main sheet container when the visible animation is complete
    // or it messes up the animation
    if (definition === 'visible') {
      containerRef.current?.focus();
    }
  };

  return (
    <Box role="presentation" className="contents" onKeyDown={(e) => trapFocus(e, containerRef)}>
      <Grid className="fixed inset-0 z-[3000] grid-cols-1 grid-rows-1 overflow-auto overscroll-y-contain px-4 pt-20 pb-0 md:pt-28">
        <motion.div
          onAnimationComplete={(definition: any) => {
            onComplete(definition);
          }}
          variants={boxVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          className="relative z-10 flex justify-center [grid-area:1/1/-1/-1]"
        >
          <Box
            ref={containerRef}
            tabIndex={-1}
            role="dialog"
            aria-labelledby={dialogId}
            aria-modal={true}
            className="relative block min-h-full w-full max-w-360 rounded-t-2xl bg-od-bg-primary px-4 pt-16 pb-8 outline-0 md:pt-37"
          >
            <Button
              aria-label="Close"
              variant="secondary"
              size="sm"
              onClick={() => setIsVisible(false)}
              className="absolute top-4 right-4 size-9 rounded-full border-0 bg-od-bg-primary p-0"
            >
              <IconX size={24} aria-hidden="true" />
            </Button>
            <CenteredLayout entry={entry} dialogId={dialogId} />
          </Box>
        </motion.div>
        <motion.div
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          className="z-0 [grid-area:1/1/-1/-1]"
        >
          <Box
            onClick={() => setIsVisible(false)}
            aria-hidden={true}
            aria-label="Close sheet"
            className="fixed inset-0 bg-od-bg-scrim/20"
          />
        </motion.div>
      </Grid>
    </Box>
  );
};

const Sheet = ({ entry, isVisible, setIsVisible, exitComplete }: ISheetProps) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    document.body.classList.toggle('has-sheet', isVisible);
  }, [isVisible]);

  const escFunction = useCallback((event: { key: string }) => {
    if (event.key === 'Escape') {
      setIsVisible(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  return mounted
    ? createPortal(
        <AnimatePresence initial={false} onExitComplete={() => exitComplete()}>
          {isVisible && (
            <motion.div key={`sheet-${entry.sys.id}`}>
              <SheetContent entry={entry} setIsVisible={setIsVisible} />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )
    : null;
};

export default Sheet;
